import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabaseClient'
import toast from 'react-hot-toast'
import useDocumentTitle from '../hooks/useDocumentTitle'

export default function ResetPasswordPage() {
  useDocumentTitle('Reset Password')

  const { updatePassword, signOut } = useAuth()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [checkingSession, setCheckingSession] = useState(true)
  const [isRecoverySession, setIsRecoverySession] = useState(false)

  useEffect(() => {
    let isMounted = true

    const resolveRecoverySession = async () => {
      const hashParams = new URLSearchParams(window.location.hash.slice(1))
      const hasRecoveryHash =
        hashParams.get('type') === 'recovery' ||
        hashParams.has('access_token') ||
        hashParams.has('refresh_token')

      const { data, error } = await supabase.auth.getSession()

      if (!isMounted) return

      if (error) {
        toast.error(error.message || 'Unable to validate reset session')
      }

      setIsRecoverySession(Boolean(data.session?.user) || hasRecoveryHash)
      setCheckingSession(false)
    }

    resolveRecoverySession()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return

      if (event === 'PASSWORD_RECOVERY') {
        setIsRecoverySession(true)
      } else if (event === 'SIGNED_OUT' && !session) {
        setIsRecoverySession(false)
      }

      setCheckingSession(false)
    })

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!password.trim() || !confirmPassword.trim()) {
      toast.error('Please fill in all fields')
      return
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    setSubmitting(true)

    try {
      await updatePassword(password)
      signOut().catch((err) => {
        console.error('Background sign out failed after password reset:', err)
      })
      navigate('/login', {
        replace: true,
        state: {
          message:
            'Password changed successfully. You can sign in with your new password.',
        },
      })
    } catch (err) {
      toast.error(err.message || 'Unable to update password')
    } finally {
      setSubmitting(false)
    }
  }

  if (checkingSession) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <div className="login-page">
      <div className="login-left">
        <div className="login-branding">
          <div className="login-logo">
            <div className="logo-icon large">K</div>
          </div>
          <h1>Reset your password</h1>
          <p>Create a new password for your Kleezy Admin account</p>
        </div>

        <div className="login-decoration">
          <div className="decoration-circle c1" />
          <div className="decoration-circle c2" />
          <div className="decoration-circle c3" />
        </div>
      </div>

      <div className="login-right">
        <div className="login-form-container">
          <div className="login-form-header">
            <h2>Set new password</h2>
            <p>
              {isRecoverySession
                ? 'Choose a strong password, then sign in again with the updated password.'
                : 'This reset link is invalid or has expired. Request a new password reset email from the login page.'}
            </p>
          </div>

          {isRecoverySession ? (
            <form className="login-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="new-password">New password</label>
                <input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                  disabled={submitting}
                  autoComplete="new-password"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirm-password">Confirm new password</label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your new password"
                  disabled={submitting}
                  autoComplete="new-password"
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="loading-spinner small" />
                    Updating password...
                  </>
                ) : (
                  'Update password'
                )}
              </button>
            </form>
          ) : (
            <div className="auth-message auth-message-warning">
              Password reset link is no longer valid. Please return to login
              and request a new email.
            </div>
          )}

          <div className="auth-link-group">
            <Link to="/login" className="btn btn-ghost auth-link-button">
              Back to login
            </Link>
          </div>

          <div className="login-footer">
            <p>
              Kleezy Admin Panel &middot; {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
